import { useMemo, useState } from 'react';
import type { ReleaseDto } from '../../../lib/api/abnahme';
import { ChartKarte, KAT_1, KAT_2, Legende, tagKurz, tagLang } from './viz';

const TAG_MS = 86_400_000;

/** Achsenmarken: Monatsanfänge, bei kurzen Spannen (unter zwei Monaten) Wochenanfänge. */
function marken(von: number, bis: number): { zeit: number; label: string }[] {
  if (bis - von > 60 * TAG_MS) {
    return monatsAnfaenge(von, bis).map((zeit) => ({
      zeit,
      label: new Date(zeit).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }),
    }));
  }
  const start = new Date(von);
  const montag = new Date(start.getFullYear(), start.getMonth(), start.getDate() + ((8 - start.getDay()) % 7 || 7));
  const ergebnis: { zeit: number; label: string }[] = [];
  for (let d = montag; d.getTime() <= bis; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7)) {
    ergebnis.push({ zeit: d.getTime(), label: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) });
  }
  return ergebnis;
}

function monatsAnfaenge(von: number, bis: number): number[] {
  const start = new Date(von);
  const erster = new Date(start.getFullYear(), start.getMonth() + 1, 1).getTime();
  const ergebnis: number[] = [];
  for (let d = new Date(erster); d.getTime() <= bis; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    ergebnis.push(d.getTime());
  }
  return ergebnis;
}

function ReleaseDetails({ release }: { release: ReleaseDto }) {
  return (
    <div className="space-y-1">
      <div className="font-medium">
        {release.umgebung === 'live' ? 'Live' : 'Testsystem'} · {release.version ? `Version ${release.version}` : release.commit.slice(0, 8)}
      </div>
      <div className="text-muted-foreground">
        {new Date(release.deployedAt).toLocaleString('de-DE')} · {release.commits}{' '}
        {release.commits === 1 ? 'Commit' : 'Commits'}
      </div>
      {release.tickets.length > 0 && <div>Tickets: {release.tickets.join(', ')}</div>}
      {release.abgenommen.length > 0 && <div>Seitdem abgenommen: {release.abgenommen.join(', ')}</div>}
    </div>
  );
}

/**
 * Zeitleiste der Releases — wie Meilensteine in einem Projektplan.
 *
 * Live-Deploys sind die Releases (Raute, obere Spur); Deploys aufs Testsystem
 * (Punkt, untere Spur) lassen sich zuschalten und sind anfangs an, solange es
 * noch kein Live-Release gibt.
 */
export default function ReleaseTimeline({
  releases,
  bis,
  titel = 'Releases',
}: {
  releases: ReleaseDto[];
  /** Ende der Achse (Stichtag), sonst heute. */
  bis?: string;
  titel?: string;
}) {
  const hatLive = releases.some((r) => r.umgebung === 'live');
  const hatTest = releases.some((r) => r.umgebung === 'test');
  const [mitTest, setMitTest] = useState(!hatLive);
  const [aktiv, setAktiv] = useState<number | null>(null);

  const sichtbar = useMemo(
    () => releases.filter((r) => r.umgebung === 'live' || mitTest),
    [releases, mitTest],
  );
  const ende = bis ? new Date(`${bis}T23:59:59`).getTime() : Date.now();
  const anfang = useMemo(() => {
    const zeiten = sichtbar.map((r) => new Date(r.deployedAt).getTime());
    const frueh = zeiten.length ? Math.min(...zeiten) : ende - 30 * TAG_MS;
    // Etwas Luft links, mindestens zwei Wochen Spanne.
    return Math.min(frueh - 2 * TAG_MS, ende - 14 * TAG_MS);
  }, [sichtbar, ende]);
  const lage = (zeit: number) => `${(((zeit - anfang) / (ende - anfang)) * 100).toFixed(2)}%`;

  const legende = (
    <div className="flex flex-wrap items-center gap-4">
      <Legende
        eintraege={[
          { label: 'Live-Release', farbe: KAT_1, form: 'raute' },
          ...(mitTest ? [{ label: 'Deploy Testsystem', farbe: KAT_2, form: 'punkt' as const }] : []),
        ]}
      />
      {hatTest && (
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={mitTest} onChange={(e) => setMitTest(e.target.checked)} />
          Testsystem-Deploys zeigen
        </label>
      )}
    </div>
  );

  const aktivRelease = sichtbar.find((r) => r.id === aktiv) ?? null;

  return (
    <ChartKarte
      titel={titel}
      beschreibung="Jeder Deploy meldet sich selbst — mit Version, behobenen Tickets und den Abnahmen seit dem vorigen."
      legende={legende}
      tabelle={{
        spalten: ['Datum', 'Umgebung', 'Version', 'Commits', 'Tickets', 'Abgenommen'],
        zeilen: [...sichtbar]
          .reverse()
          .map((r) => [
            tagLang(r.deployedAt),
            r.umgebung === 'live' ? 'Live' : 'Testsystem',
            r.version ?? r.commit.slice(0, 8),
            r.commits,
            r.tickets.join(', ') || '—',
            r.abgenommen.join(', ') || '—',
          ]),
      }}
    >
      {sichtbar.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {hatTest
            ? 'Noch kein Live-Release — Testsystem-Deploys lassen sich oben zuschalten.'
            : 'Noch keine Deploys erfasst. Ab jetzt meldet sich jeder Deploy selbst.'}
        </p>
      ) : (
        <div className="relative select-none pb-6 pt-2" onMouseLeave={() => setAktiv(null)}>
          {/* Spuren */}
          <div className="relative h-20">
            <div className="absolute inset-x-0 top-6 border-t" style={{ borderColor: 'var(--viz-raster)' }} />
            {mitTest && (
              <div className="absolute inset-x-0 top-14 border-t border-dashed" style={{ borderColor: 'var(--viz-raster)' }} />
            )}
            {marken(anfang, ende).map(({ zeit, label }) => (
              <div
                key={zeit}
                className="absolute top-0 h-20 border-l"
                style={{ left: lage(zeit), borderColor: 'var(--viz-raster)' }}
              >
                <span className="absolute top-20 -translate-x-1/2 whitespace-nowrap pt-1 text-[11px] text-muted-foreground">
                  {label}
                </span>
              </div>
            ))}
            {/* Heute bzw. Stichtag */}
            <div className="absolute right-0 top-0 h-20 border-l-2 border-foreground/40">
              <span className="absolute -top-0.5 right-1 whitespace-nowrap text-[11px] text-muted-foreground">
                {bis ? `Stichtag ${tagKurz(bis)}` : 'heute'}
              </span>
            </div>
            {sichtbar.map((release) => {
              const live = release.umgebung === 'live';
              return (
                <button
                  key={release.id}
                  type="button"
                  aria-label={`${live ? 'Live-Release' : 'Deploy Testsystem'} ${release.version ?? release.commit.slice(0, 8)} am ${tagLang(release.deployedAt)}`}
                  onMouseEnter={() => setAktiv(release.id)}
                  onFocus={() => setAktiv(release.id)}
                  onBlur={() => setAktiv(null)}
                  className="absolute flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded focus-visible:outline focus-visible:outline-2"
                  style={{ left: lage(new Date(release.deployedAt).getTime()), top: live ? 24 : 56 }}
                >
                  {live ? (
                    <span
                      className="block h-3 w-3 rotate-45 ring-2"
                      style={{ background: KAT_1, ['--tw-ring-color' as string]: 'var(--viz-flaeche)' }}
                    />
                  ) : (
                    <span
                      className="block h-2 w-2 rounded-full ring-2"
                      style={{ background: KAT_2, ['--tw-ring-color' as string]: 'var(--viz-flaeche)' }}
                    />
                  )}
                  {live && release.version && (
                    <span className="pointer-events-none absolute -top-4 whitespace-nowrap text-[11px] font-medium text-foreground">
                      {release.version}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {aktivRelease && (
            <div
              role="tooltip"
              className="absolute z-10 w-64 -translate-x-1/2 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
              style={{
                left: `clamp(8rem, ${lage(new Date(aktivRelease.deployedAt).getTime())}, calc(100% - 8rem))`,
                top: aktivRelease.umgebung === 'live' ? 40 : 72,
              }}
            >
              <ReleaseDetails release={aktivRelease} />
            </div>
          )}
        </div>
      )}
    </ChartKarte>
  );
}
